import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaInstagram, FaFacebookF, FaYoutube, FaLinkedinIn } from 'react-icons/fa';
import { useSiteContent } from '../../context/SiteContentContext';
import API_URL from '../../config/api';
import './Footer.css';

const DEFAULT_QUICK_LINKS = [
  { label: 'Home', path: '/' },
  { label: 'Products', path: '/products' },
  { label: 'Certificates', path: '/certificates' },
  { label: 'Catalog', path: '/catalog' },
  { label: 'News & Blogs', path: '/news-blogs' },
  { label: 'Inquiry', path: '/contact#inquiry-form' },
];

const DEFAULT_PRODUCTS = [
  { label: 'Tube Fittings', category: 'Tube Fittings' },
  { label: 'Valves', category: 'Valves' },
  { label: 'Double block & bleed valves', category: 'Double block & bleed valves' },
  { label: 'Manifolds', category: 'Manifolds' },
  { label: 'Flange adaptors', category: 'Flange adaptors' },
  { label: 'Regulators', category: 'Regulators' },
  { label: 'Filters', category: 'Filters' },
  { label: 'Swivel gauge adaptor', category: 'Swivel gauge adaptor' },
  { label: 'Air manifolds', category: 'Air manifolds' },
  { label: 'Condensate pot', category: 'Condensate pot' },
  { label: 'Sampling system', category: 'Sampling system' },
  { label: 'Pressure gauge', category: 'Pressure gauge' },
  { label: 'Temperature gauge', category: 'Temperature gauge' },
  { label: 'Thermowells', category: 'Thermowells' },
  { label: 'Accessories', category: 'Accessories' },
];

const DEFAULTS = {
  company_name: 'Hari Krishna Industries (HKI)',
  phone: '+91 9167633933',
  email: 'sales@hkinds.com',
  address:
    'A-1001, Viceroy Park, Opp RTO office, Kandarpada, Dahisar-West, Mumbai-400068',
  map_url:
    'https://www.google.com/maps?q=A-1001,+Viceroy+Park,+Opp+RTO+office,+Kandarpada,+Dahisar-West,+Mumbai-400068',
  instagram_url:
    'https://www.instagram.com/harikrishnaaindustries?igsh=MXAydzl2Y2V2aWNlcA==',
  facebook_url: 'https://facebook.com',
  youtube_url: 'https://youtube.com',
  linkedin_url:
    'https://www.linkedin.com/company/hari-krishnaa-industries-hki/?viewAsMember=true',
  copyright: 'Hari Krishna Industries',
  quick_links_heading: 'Quick Links',
  products_heading: 'Products',
  contact_heading: 'Contact',
  map_label: 'View on Map',
  newsletter_heading: 'Subscribe to Our Newsletter',
  newsletter_placeholder: 'Enter your email',
  newsletter_button_text: 'Subscribe',
  social_heading: 'Follow Us',
};

const splitProducts = (items) => {
  const midpoint = Math.ceil(items.length / 2);
  return [items.slice(0, midpoint), items.slice(midpoint)];
};

const Footer = () => {
  const { siteContent } = useSiteContent();
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterMessage, setNewsletterMessage] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState('');
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);

  const content = {
    company_name: siteContent?.footer_company_name || DEFAULTS.company_name,
    phone: siteContent?.footer_phone || DEFAULTS.phone,
    email: siteContent?.footer_email || DEFAULTS.email,
    address: siteContent?.footer_address || DEFAULTS.address,
    map_url: siteContent?.footer_map_url || DEFAULTS.map_url,
    instagram_url: siteContent?.footer_instagram_url || DEFAULTS.instagram_url,
    facebook_url: siteContent?.footer_facebook_url || DEFAULTS.facebook_url,
    youtube_url: siteContent?.footer_youtube_url || DEFAULTS.youtube_url,
    linkedin_url: siteContent?.footer_linkedin_url || DEFAULTS.linkedin_url,
    copyright: siteContent?.footer_copyright || DEFAULTS.copyright,
    quick_links_heading:
      siteContent?.footer_quick_links_heading || DEFAULTS.quick_links_heading,
    products_heading:
      siteContent?.footer_products_heading || DEFAULTS.products_heading,
    contact_heading:
      siteContent?.footer_contact_heading || DEFAULTS.contact_heading,
    map_label: siteContent?.footer_map_label || DEFAULTS.map_label,
    newsletter_heading:
      siteContent?.footer_newsletter_heading || DEFAULTS.newsletter_heading,
    newsletter_placeholder:
      siteContent?.footer_newsletter_placeholder || DEFAULTS.newsletter_placeholder,
    newsletter_button_text:
      siteContent?.footer_newsletter_button_text || DEFAULTS.newsletter_button_text,
    social_heading:
      siteContent?.footer_social_heading || DEFAULTS.social_heading,
  };

  const quickLinks =
    Array.isArray(siteContent?.footer_quick_links) && siteContent.footer_quick_links.length > 0
      ? siteContent.footer_quick_links
      : DEFAULT_QUICK_LINKS;

  const products =
    Array.isArray(siteContent?.footer_products) && siteContent.footer_products.length > 0
      ? siteContent.footer_products
      : DEFAULT_PRODUCTS;

  const [productColumnOne, productColumnTwo] = splitProducts(products);
  const phoneHref = `tel:${String(content.phone).replace(/[^\d+]/g, '')}`;
  const emailHref = `mailto:${content.email}`;

  const handleNewsletterSubmit = async (event) => {
    event.preventDefault();

    const email = newsletterEmail.trim();
    if (!email) {
      return;
    }

    try {
      setNewsletterSubmitting(true);
      setNewsletterStatus('');
      setNewsletterMessage('');

      const response = await fetch(API_URL.NEWSLETTER_SUBSCRIBERS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          email,
          source: 'footer',
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Unable to subscribe right now.');
      }

      setNewsletterStatus('success');
      setNewsletterMessage(data.message || 'Subscribed successfully.');
      setNewsletterEmail('');
    } catch (error) {
      setNewsletterStatus('error');
      setNewsletterMessage(error.message || 'Unable to subscribe right now.');
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  return (
    <footer className="footer">
      <div className="footer-container">
        <div className="footer-links">
          <h3>{content.quick_links_heading}</h3>
          <ul>
            {quickLinks.map((item, index) => (
              <li key={`${item.label}-${index}`}>
                <Link to={item.path || '/'}>{item.label || 'Link'}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="footer-links">
          <h3>{content.products_heading}</h3>
          <div className="footer-products-lists">
            <ul>
              {productColumnOne.map((item, index) => (
                <li key={`${item.label}-${index}`}>
                  <Link
                    to={item.category ? `/products?category=${encodeURIComponent(item.category)}` : '/products'}
                  >
                    {item.label || 'Product'}
                  </Link>
                </li>
              ))}
            </ul>
            <ul>
              {productColumnTwo.map((item, index) => (
                <li key={`${item.label}-${index}`}>
                  <Link
                    to={item.category ? `/products?category=${encodeURIComponent(item.category)}` : '/products'}
                  >
                    {item.label || 'Product'}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="footer-address">
          <h3>{content.contact_heading}</h3>
          <div className="address-info">
            <p>
              <strong>{content.company_name}</strong>
            </p>
            <p>{content.address}</p>
            <p>
              <a href={phoneHref}>{content.phone}</a>
            </p>
            <p>
              <a href={emailHref}>{content.email}</a>
            </p>
            {content.map_url && (
              <p>
                <a href={content.map_url} target="_blank" rel="noopener noreferrer">
                  {content.map_label}
                </a>
              </p>
            )}
          </div>

          <div className="footer-news-follow">
            <div className="footer-newsletter">
              <h3>{content.newsletter_heading}</h3>
              <form onSubmit={handleNewsletterSubmit}>
                <input
                  type="email"
                  placeholder={content.newsletter_placeholder}
                  value={newsletterEmail}
                  onChange={(event) => setNewsletterEmail(event.target.value)}
                  required
                />
                <button type="submit" disabled={newsletterSubmitting}>
                  {newsletterSubmitting ? 'Submitting...' : content.newsletter_button_text}
                </button>
              </form>
              {newsletterMessage && (
                <p className={`footer-newsletter-message ${newsletterStatus}`}>
                  {newsletterMessage}
                </p>
              )}
            </div>

            <div className="footer-follow">
              <h3>{content.social_heading}</h3>
              <div className="social-icons">
                <a href={content.instagram_url} target="_blank" rel="noopener noreferrer">
                  <FaInstagram />
                </a>
                <a href={content.facebook_url} target="_blank" rel="noopener noreferrer">
                  <FaFacebookF />
                </a>
                <a href={content.youtube_url} target="_blank" rel="noopener noreferrer">
                  <FaYoutube />
                </a>
                <a href={content.linkedin_url} target="_blank" rel="noopener noreferrer">
                  <FaLinkedinIn />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="footer-bottom">
        <p>&copy; {new Date().getFullYear()} {content.copyright}. All Rights Reserved.</p>
      </div>
    </footer>
  );
};

export default Footer;
